import React, { useRef, useState } from "react";
import axios from "axios";
import { Film, Upload, Loader2, CheckCircle2, AlertCircle, X } from "lucide-react";
import API_BASE from "../config/api";
import { auth } from "../config/config-firebase";

// Converts an uploaded QuickTime (.mov) video to an MP4 with H.264 video + AAC audio, which plays on Roku.
//  - Detects MOV by file extension, MIME type (video/quicktime) and the file's own "ftyp qt" header.
//  - MOV  -> uploaded to POST /api/video/convert-mov (ffmpeg on the server) -> onConverted(mp4File)
//  - other video files (e.g. MP4) are passed straight through to onConverted unchanged.
//
// Props:
//   customerId    required, the store the request is made for (merchantAuth checks it)
//   onConverted   (file: File, info: { converted: boolean, originalName: string }) => void
//   onError       optional (message: string) => void
//   maxSizeMB     default 500 (the server enforces the same limit)
//   disabled, showHeader (false when embedded in a card that has its own title), className

const MAX_DEFAULT_MB = 500;
const CONVERT_TIMEOUT_MS = 15 * 60 * 1000;

const formatSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** True if the file looks like a QuickTime movie: extension, MIME type, or an "ftyp qt  " header. */
export async function isMovFile(file) {
  if (!file) return false;
  if (/\.mov$/i.test(file.name) || file.type === "video/quicktime") return true;
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    const text = (from, to) => String.fromCharCode(...head.slice(from, to));
    return text(4, 8) === "ftyp" && text(8, 12) === "qt  ";
  } catch {
    return false;
  }
}

/** Calls the backend converter. Resolves to an MP4 File (H.264 + AAC). */
export async function convertMovToMp4(file, { customerId, onUploadProgress, signal } = {}) {
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
  const body = new FormData();
  body.append("video", file);

  try {
    const res = await axios.post(`${API_BASE}/video/convert-mov`, body, {
      params: { customerId },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      responseType: "blob",
      timeout: CONVERT_TIMEOUT_MS,
      signal,
      onUploadProgress: (e) => e.total && onUploadProgress && onUploadProgress(Math.round((e.loaded / e.total) * 100)),
    });
    const name = `${file.name.replace(/\.[^.]+$/, "") || "video"}.mp4`;
    return new File([res.data], name, { type: "video/mp4" });
  } catch (err) {
    // Error bodies come back as a Blob because of responseType: "blob"; read the JSON message out of it.
    let message = err.message;
    if (err.response?.data instanceof Blob) {
      try {
        message = JSON.parse(await err.response.data.text()).message || message;
      } catch { /* keep default message */ }
    }
    if (err.code === "ERR_CANCELED") throw err;
    throw new Error(message || "Conversion failed");
  }
}

export default function MovToMp4Converter({ customerId, onConverted, onError, maxSizeMB = MAX_DEFAULT_MB, disabled = false, showHeader = true, className = "" }) {
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | uploading | converting | done | error
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null); // { name, size, converted }

  const busy = phase === "uploading" || phase === "converting";

  const fail = (msg) => {
    setPhase("error");
    setMessage(msg);
    if (onError) onError(msg);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/") && !/\.(mov|mp4|m4v)$/i.test(file.name)) return fail("Please choose a video file (MOV or MP4).");
    if (file.size > maxSizeMB * 1048576) return fail(`${file.name} is larger than ${maxSizeMB} MB.`);
    if (!customerId) return fail("No store selected.");

    setResult(null);
    setMessage("");
    setProgress(0);

    if (!(await isMovFile(file))) {
      // Not a MOV: nothing to convert, hand the original back.
      setResult({ name: file.name, size: file.size, converted: false });
      setPhase("done");
      setMessage("Not a MOV file, so no conversion was needed.");
      if (onConverted) onConverted(file, { converted: false, originalName: file.name });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setPhase("uploading");
    try {
      const mp4 = await convertMovToMp4(file, {
        customerId,
        signal: controller.signal,
        onUploadProgress: (pct) => {
          setProgress(pct);
          if (pct >= 100) setPhase("converting");
        },
      });
      setResult({ name: mp4.name, size: mp4.size, converted: true });
      setPhase("done");
      setMessage("Converted to MP4 (H.264 video, AAC audio).");
      if (onConverted) onConverted(mp4, { converted: true, originalName: file.name });
    } catch (err) {
      if (err.code === "ERR_CANCELED") {
        setPhase("idle");
        setMessage("");
      } else {
        fail(err.message);
      }
    } finally {
      abortRef.current = null;
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // lets the same file be chosen again
    handleFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!busy && !disabled) handleFile(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  return (
    <div className={`w-full rounded-xl border border-gray-200 bg-white p-4 ${className}`}>
      {showHeader && (
        <div className="mb-3 flex items-center gap-2">
          <Film className="h-5 w-5 text-gray-700" />
          <h3 className="text-sm font-semibold text-gray-900">MOV to MP4 converter</h3>
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center ${
          disabled || busy ? "border-gray-200 bg-gray-50" : "border-gray-300 hover:border-gray-400"
        }`}
      >
        {busy ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
            <p className="text-sm text-gray-700">{phase === "uploading" ? `Uploading… ${progress}%` : "Converting to H.264 + AAC MP4… this can take a few minutes."}</p>
            <div className="h-1.5 w-full max-w-xs overflow-hidden rounded bg-gray-200">
              <div className={`h-full bg-gray-800 ${phase === "converting" ? "w-full animate-pulse" : ""}`} style={phase === "uploading" ? { width: `${progress}%` } : undefined} />
            </div>
            <button type="button" onClick={() => abortRef.current && abortRef.current.abort()} className="mt-1 inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
              <X className="h-3 w-3" /> Cancel
            </button>
          </>
        ) : (
          <>
            <Upload className="h-6 w-6 text-gray-500" />
            <p className="text-sm text-gray-700">Drop a .mov video here, or</p>
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current && inputRef.current.click()}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Choose video
            </button>
            <p className="text-xs text-gray-500">Output: MP4 · H.264 video · AAC audio (plays on Roku). Up to {maxSizeMB} MB.</p>
          </>
        )}
        <input ref={inputRef} type="file" accept=".mov,.mp4,.m4v,video/quicktime,video/mp4" className="hidden" onChange={onInputChange} />
      </div>

      {phase === "done" && result && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">{result.name} <span className="font-normal text-green-700">({formatSize(result.size)})</span></p>
            <p className="text-xs">{message}</p>
          </div>
        </div>
      )}
      {phase === "error" && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{message}</p>
        </div>
      )}
    </div>
  );
}
